import { getAppConfig, getChatbotApiKey, getChatbotRetryAttempts, getChatbotTimeoutMs } from '../utils/config';
import { logger } from '../utils/logger';

import { ConversationManager } from './conversation';
import { request } from './request';
import { storylineContextManager } from './storylineContext';
import type {
  ChatOptions,
  ChatPrompt,
  ChatRequestPayload,
  ChatResponse,
  SLGuideContext,
  VisionOptions,
  VisionPrompt,
} from './types';

// Conversation manager instance
const conversationManager = new ConversationManager();

// Create component logger for LLM service
const llmLogger = logger.withComponent('LLMService');

// Dev context loaded lazily
let devContext: SLGuideContext | null = null;

export async function chat(prompt: ChatPrompt, options: ChatOptions = {}): Promise<ChatResponse> {
  // Get API base URL from configuration
  const config = getAppConfig();
  const endpoint = `${config.apiBaseUrl}/api/v1/chat`;

  // Get current conversation state
  const sessionInfo = conversationManager.getSessionInfo();

  // Build request payload
  const payload: ChatRequestPayload = {
    message: prompt.message,
  };

  // Add image URL if provided
  if (prompt.imageUrl) {
    payload.image_data_b64 = prompt.imageUrl;
  }

  // Add session info if available
  if (sessionInfo.sessionId) {
    payload.session_id = sessionInfo.sessionId;
  } else {
    const sessionData = localStorage.getItem("core_setup_session_id");
    let sessionId = '';

    if (sessionData) {
      try {
        const parsed = JSON.parse(sessionData);
        sessionId = parsed.value || '';
      } catch (e) {
        llmLogger.error("Invalid JSON in localStorage:", e);
      }
    }

    payload.session_id = sessionId;
  }
  if (sessionInfo.previousResponseId) {
    payload.previous_response_id = sessionInfo.previousResponseId;
  }

  // Add Storyline context - priority order:
  // 1. Explicitly passed context (highest priority)
  // 2. Auto-injected Storyline context from postMessage
  // 3. Dev context from file (lowest priority)
  const storylineContext = storylineContextManager.getContext();
  const slContext = options.slGuideContext || 
    (storylineContextManager.hasContext() ? storylineContext : null) || 
    devContext;
    
  if (slContext) {
    payload.sl_guide_context = slContext;

    // Log context source in debug mode
    if (options.slGuideContext) {
      llmLogger.debug('📋 Using explicitly passed context for API call');
    } else if (storylineContextManager.hasContext()) {
      llmLogger.debug('🎯 Using auto-injected Storyline context');
    } else {
      llmLogger.debug('🔧 Using dev context for API call');
    }
  }

  // Add flow variables if provided
  if (options.flowVariables) {
    payload.flow_variables = options.flowVariables;
  }

  // Add language for AI response localization
  if (options.language) {
    payload.language = options.language;
  }

  // Log the request in debug mode
  llmLogger.info('🚀 LLM Request sent', {
    endpoint,
    message: prompt.message,
    sessionId: payload.session_id || 'new session',
    previousResponseId: payload.previous_response_id || 'none',
    hasContext: !!payload.sl_guide_context,
  });

  try {
    // Make the API call
    const response = await request<ChatResponse>(endpoint, {
      method: 'POST',
      headers: {
        'X-API-Key': getChatbotApiKey(),
      },
      body: JSON.stringify(payload),
      signal: options.abortSignal,
      timeout: options.timeout || getChatbotTimeoutMs(),
      retries: options.retries || getChatbotRetryAttempts(),
    });

    // Update conversation state
    conversationManager.updateSession(response.session_id, response.response_id);

    return response;
  } catch (error) {
    llmLogger.error('❌ LLM Request failed', error);
    throw error;
  }
}

export async function sendVision(prompt: VisionPrompt, options: VisionOptions = {}): Promise<ChatResponse> {
  // Convert to ChatPrompt and use the regular chat function
  const chatPrompt: ChatPrompt = {
    message: prompt.message,
    imageUrl: prompt.imageUrl,
  };

  return await chat(chatPrompt, options);
}

// Export conversation manager for external use
export { conversationManager }; 